/**
 * The Inference_Configuration page (route `/inference`; Feature: customer-managed-inference,
 * tasks 17.1–17.3; **Requirements 5.5, 5.7, 5.8, 7.4, 7.6, 7.7, 7.9, 10.2, 13.4, 13.5**;
 * Design: §Components 22, §Error Handling).
 *
 * Where an administrator says *whose AWS account processes this Account's documentation and whose bill
 * it lands on*. It is built on the {@link import('./ApiKeysPage').ApiKeysPage} structure — one
 * `LoadState`, {@link ConfirmDialog} in front of the one write with an irreversible consequence, a
 * page-level `role="status"` notice, {@link useWrite} so a failed write cannot touch what is displayed
 * — because the two pages have the same shape of problem: an admin-only read, a credential-adjacent
 * value on screen, and writes whose consequences land outside the platform.
 *
 * ## The whole page is admin-only, and the gate withholds the request
 *
 * Every `inference-config:*` action is granted to `admin` alone (Requirements 14.2, 14.3), so a viewer
 * or an editor cannot read the configuration at all — "viewer sees, admin acts" is not available here
 * any more than it is on the API-keys page. So the gate is **ahead of the fetch, not a branch after
 * it**: a non-admin gets the Requirement 13.5 explanation and **no `GET` is issued**, for either route.
 *
 * That is not just tidiness about wasted requests. Rendering optimistically and letting the `403`
 * produce an error panel would tell an editor that something is broken, when the true answer is that
 * inference configuration belongs to an administrator. And the read carries the External_Id and the
 * whole Published_Role_Policy, so withholding it is also the narrower disclosure: an editor sees
 * neither, which is the second half of Requirement 13.5.
 *
 * The role comes from {@link useSelectedAccount} — the role **in the selected Account** — and never
 * from the ID token, whose `role` is the home Account's.
 *
 * ## The External_Id is shown unmasked, on purpose
 *
 * Requirement 1.6 returns it unmasked and this page renders it that way, with a copy control. It is
 * not a credential: it is an anti-confused-deputy nonce, possession alone reaches nothing because an
 * attacker also has to *be* the Inference_Principal, and an administrator cannot write the
 * `sts:ExternalId` condition on their trust policy without reading the value. Masking it would break
 * the one thing it exists for. What it gets instead is exclusion from every error body and every log
 * record (Requirements 2.9, 7.6) — which is why {@link InferenceFailurePanel} renders no part of it.
 *
 * ## Nothing here sets the effective limits (OI-D2)
 *
 * The four Requirement 10.2 figures are rendered as a definition list and **no control on this page
 * changes one**. Requirement 10.2 asks that the guards bounding a customer's own spend be theirs to
 * *see*; making them theirs to *set* is **OI-D2** and out of scope, so there is no payload field for
 * them and adding an input here would be adding a control with nothing behind it.
 *
 * ## The geography acknowledgement is derived, which is what makes Requirement 5.7 unbreakable
 *
 * A `global.`-prefixed Inference_Profile may route outside the Customer_Inference_Region's geography.
 * Requirement 5.5 wants an explicit affirmative action before one is accepted — the
 * `user-and-data-management` Requirement 6.6 pattern {@link ProvenanceNotice} also uses — and
 * Requirement 5.7 wants that acknowledgement to cover *the identifier it was given for and no other*.
 *
 * So the tick is **not stored as a boolean**. {@link acknowledgedKey} stores *which* set of identifiers
 * was acknowledged, and the checkbox's `checked` is computed by comparing it to the set currently in
 * the form. Changing a `global.` identifier therefore clears the tick **by construction** rather than
 * by an effect that has to remember to fire, and there is no state a re-render could leave stale. There
 * is no default, no inferred setting, and no pre-tick (Requirement 5.8): `acknowledgedKey` starts
 * `undefined`, and a configuration that already carries acknowledgements does not seed it — a stored
 * acknowledgement is a record of a past decision, not consent to the next write.
 *
 * **OI-U7 is live.** If `global.`-prefixed profiles are banned outright in customer-hosted mode, the
 * removal is one place: delete {@link GeographyAcknowledgement}, {@link globalProfileIds},
 * {@link acknowledgementKeyOf}, the `acknowledgedKey` state, and the checkbox it renders. The submit
 * gate collapses to `false` and the payload stops carrying `geographyAcknowledgements`. Nothing else on
 * this page is shaped around the acknowledgement existing.
 *
 * ## A failed write leaves the page exactly as it was
 *
 * Every write runs through {@link useWrite}, which touches nothing but its own in-flight flag and last
 * failure. The displayed configuration is replaced only by a **re-read**, and the re-read only happens
 * inside an `onSuccess`. So a `400` or a `409` shows the returned error beside the fields it names and
 * leaves the panels above reading exactly what they read before the request — which is what
 * Requirement 7.7 asks for from the other direction: the page must not appear to have applied
 * something it did not.
 */

import { useEffect, useState } from 'react';

import { useSelectedAccount } from '../account-context';
import { ApiError } from '../api/client';
import {
  SOURCE_TYPES,
  INFERENCE_MODES,
  type InferenceConfigurationDto,
  type InferenceConfigurationPayload,
  type InferenceFailureDto,
  type InferenceMode,
  type ModelPin,
  type ModelPins,
  type ProbeOutcome,
  type PublishedRolePolicyDto,
  type SourceType,
  type VerificationState,
} from '../api/types';
import { useApiClient } from '../api/use-api-client';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { FormErrorPanel, FormField, unmappedFieldErrors } from '../components/FieldErrorText';
import { ErrorState, LoadingState } from '../components/LoadStates';
import { CopyableSnippet } from '../terraform/CopyableSnippet';
// The mode names, the failure labels, and the remediation sentences are shared with the generation
// surfaces (task 18.1, 18.2), because a remediation is an instruction about a customer's own AWS
// account and two spellings of one would be two versions of what they are being asked to change.
import {
  FAILURE_LABELS,
  FAILURE_REMEDIATION,
  MODE_LABELS,
  isUnjudgedFailure,
} from '../lib/inference-copy';
import { useWrite } from '../lib/use-write';

/* -------------------------------------------------------------------------- */
/* Vocabulary                                                                  */
/* -------------------------------------------------------------------------- */

/** The body key the replace payload nests under, and the root of every returned `FieldError` path. */
const ROOT = 'inferenceConfiguration';

/**
 * The prefix that marks an Inference_Profile as cross-geography.
 *
 * A `global.`-prefixed profile is ACTIVE and invokes successfully while routing the request to a region
 * outside the one named — the hazard the steering rule calls out as invisible in the identifier. It is
 * banned outright in Soroush-hosted mode and admitted in customer-hosted mode only behind the
 * acknowledgement below, which is the distinction `.kiro/steering/ai-data-handling.md` rule 3 draws.
 */
const GLOBAL_PROFILE_PREFIX = 'global.';

/**
 * How the page names each {@link VerificationState}.
 *
 * Three distinct strings with no substring relationship worth mistaking: a page in the `failed` state
 * must not read as healthy (Requirement 7.7), and "Failed" pointing at the fault below is the whole of
 * that at the top of the page.
 */
const VERIFICATION_LABELS: Record<VerificationState, string> = {
  verified: 'Verified',
  unverified: 'Not yet verified',
  failed: 'Failed — see the fault below',
};

/* -------------------------------------------------------------------------- */
/* Page state                                                                  */
/* -------------------------------------------------------------------------- */

type LoadState =
  | { status: 'loading' }
  | { status: 'error'; message: string; isAuthError: boolean }
  | { status: 'loaded'; configuration: InferenceConfigurationDto };

/**
 * The Published_Role_Policy read, which has a legitimate "not available yet" arm.
 *
 * `GET …/role-policy` answers `409` until a customer configuration is stored, because the permission
 * policy's `Resource` list is derived from the region and the selections and AWS rejects an IAM
 * document with an empty `Resource`. That is a state to explain, not an error to report — the two
 * values needed *first* are on the configuration read — so it has its own arm rather than sharing the
 * error one.
 */
type RolePolicyState =
  | { status: 'loading' }
  | { status: 'unavailable'; message: string }
  | { status: 'loaded'; rolePolicy: PublishedRolePolicyDto };

/** The one write awaiting confirmation. A union, because only one dialog is ever open. */
type PendingAction = { kind: 'rotate-external-id' };

/** One Source_Type's two pin inputs, as text. */
interface PinDraft {
  primary: string;
  escalation: string;
}

/**
 * The form's own state — every field as the administrator typed it, never as the server stores it.
 *
 * Strings throughout, including the pins, so an emptied input is an empty string rather than an
 * `undefined` that has to be distinguished from "not rendered". {@link buildPayload} is the one place
 * that turns blanks back into absences.
 */
interface FormState {
  mode: InferenceMode;
  customerRoleArn: string;
  customerRegion: string;
  primary: string;
  escalation: string;
  pins: Record<SourceType, PinDraft>;
}

/** The form as it starts from a read configuration — the retained values, so nothing is re-entered. */
function formFrom(configuration: InferenceConfigurationDto): FormState {
  const pins = {} as Record<SourceType, PinDraft>;
  for (const sourceType of SOURCE_TYPES) {
    const pin: ModelPin = configuration.pins?.[sourceType] ?? {};
    pins[sourceType] = { primary: pin.primary ?? '', escalation: pin.escalation ?? '' };
  }
  return {
    mode: configuration.mode,
    customerRoleArn: configuration.customerRoleArn ?? '',
    customerRegion: configuration.customerRegion ?? '',
    primary: configuration.profiles?.primary ?? '',
    escalation: configuration.profiles?.escalation ?? '',
    pins,
  };
}

/**
 * Every Inference_Profile identifier in the form that is `global.`-prefixed, deduplicated and sorted.
 *
 * Sorted so the acknowledgement key is stable under reordering: moving the same identifier from the
 * default primary to a Model_Pin is not a new geography decision, and clearing the tick for it would be
 * an acknowledgement that appears to lapse for no reason — the kind of prompt an administrator learns
 * to click through. Adding, removing, or *changing* one does move the set, which is Requirement 5.7.
 */
function globalProfileIds(form: FormState): string[] {
  const candidates = [
    form.primary,
    form.escalation,
    ...SOURCE_TYPES.flatMap((sourceType) => [
      form.pins[sourceType].primary,
      form.pins[sourceType].escalation,
    ]),
  ];
  const ids = candidates
    .map((value) => value.trim())
    .filter((value) => value.startsWith(GLOBAL_PROFILE_PREFIX));
  return [...new Set(ids)].sort();
}

/**
 * The acknowledgement's key: the exact identifier set it covers.
 *
 * `\u0000` as the separator because it cannot occur in a Bedrock identifier, so no two distinct sets
 * can collide into one key — a joined-on-comma key would let `["a,b"]` and `["a","b"]` agree.
 */
function acknowledgementKeyOf(globalIds: readonly string[]): string {
  return globalIds.join('\u0000');
}

/* -------------------------------------------------------------------------- */
/* The payload                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * The `PUT` body for the current form — conditional spreads throughout, **never a present
 * `undefined`**.
 *
 * That is the same discipline the server-side record keeps, for the same reason: a key present holding
 * `undefined` survives `JSON.stringify` as an absent key but is observably present to `in` and to a
 * deep-equality assertion, so a payload built with `{ pins: maybeUndefined }` would send `{}` over the
 * wire while every local assertion about its shape disagreed. `exactOptionalPropertyTypes` is not
 * enabled in this tree, so the compiler catches none of it.
 *
 * A blank input becomes an **absence**, and on a whole-record replace an absence is a removal: emptying
 * a Model_Pin removes it. The profile pair is sent whole whenever either half is filled, so a
 * half-filled pair earns the server's own "name a primary and an escalation" error rather than being
 * silently dropped.
 */
function buildPayload(
  form: FormState,
  acknowledgedIds: readonly string[],
): InferenceConfigurationPayload {
  const roleArn = form.customerRoleArn.trim();
  const region = form.customerRegion.trim();
  const primary = form.primary.trim();
  const escalation = form.escalation.trim();

  const pins: ModelPins = {};
  for (const sourceType of SOURCE_TYPES) {
    const pinPrimary = form.pins[sourceType].primary.trim();
    const pinEscalation = form.pins[sourceType].escalation.trim();
    if (pinPrimary.length === 0 && pinEscalation.length === 0) continue;
    pins[sourceType] = {
      ...(pinPrimary.length === 0 ? {} : { primary: pinPrimary }),
      ...(pinEscalation.length === 0 ? {} : { escalation: pinEscalation }),
    };
  }

  return {
    mode: form.mode,
    ...(roleArn.length === 0 ? {} : { customerRoleArn: roleArn }),
    ...(region.length === 0 ? {} : { customerRegion: region }),
    ...(primary.length === 0 && escalation.length === 0 ? {} : { profiles: { primary, escalation } }),
    ...(Object.keys(pins).length === 0 ? {} : { pins }),
    ...(acknowledgedIds.length === 0
      ? {}
      : { geographyAcknowledgements: acknowledgedIds.map((profileId) => ({ profileId })) }),
  };
}

/** Every `FieldError` path the form renders a control for; the rest go to {@link FormErrorPanel}. */
function mappedPaths(): string[] {
  return [
    `${ROOT}.mode`,
    `${ROOT}.customerRoleArn`,
    `${ROOT}.customerRegion`,
    `${ROOT}.profiles`,
    `${ROOT}.profiles.primary`,
    `${ROOT}.profiles.escalation`,
    ...SOURCE_TYPES.flatMap((sourceType) => [
      `${ROOT}.pins.${sourceType}.primary`,
      `${ROOT}.pins.${sourceType}.escalation`,
    ]),
  ];
}

/* -------------------------------------------------------------------------- */
/* The page                                                                    */
/* -------------------------------------------------------------------------- */

export function InferenceConfigurationPage(): JSX.Element {
  const client = useApiClient();
  // The role in the SELECTED Account, not the token's. Every `inference-config:*` action is admin-only,
  // so this gates the read as well as the writes — see the module doc.
  const { accountId, role } = useSelectedAccount();
  const isAdmin = role === 'admin';

  const [state, setState] = useState<LoadState>({ status: 'loading' });
  const [rolePolicy, setRolePolicy] = useState<RolePolicyState>({ status: 'loading' });
  const [reloadToken, setReloadToken] = useState(0);
  const [form, setForm] = useState<FormState | undefined>(undefined);
  /**
   * *Which* identifier set the administrator ticked for, not *whether* they ticked (Requirement 5.7).
   *
   * Starts `undefined` and is never seeded from the stored acknowledgements — no default, no inferred
   * setting, no pre-tick (Requirement 5.8).
   */
  const [acknowledgedKey, setAcknowledgedKey] = useState<string | undefined>(undefined);
  const [pending, setPending] = useState<PendingAction | undefined>(undefined);
  const [notice, setNotice] = useState<string | undefined>(undefined);

  // Three runners: the replace form, the verify button, and the rotate dialog fail independently, and a
  // failure in one must not blank another's message.
  const replace = useWrite();
  const verify = useWrite();
  const rotate = useWrite();

  useEffect(() => {
    // Requirement 13.5 — the gate withholds the requests, not just the affordances. A non-admin issues
    // no `GET` on either route, so neither the External_Id nor the policy is fetched for them.
    if (!isAdmin) return;
    let active = true;
    setState({ status: 'loading' });
    setRolePolicy({ status: 'loading' });

    void client
      .getInferenceConfiguration()
      .then((response) => {
        if (!active) return;
        setState({ status: 'loaded', configuration: response.inferenceConfiguration });
        // The form is re-seeded from every successful read, which is what makes "re-read only after a
        // successful write" visible: a failed write leaves both the panels and this draft alone.
        setForm(formFrom(response.inferenceConfiguration));
      })
      .catch((error: unknown) => {
        if (!active) return;
        const message =
          error instanceof Error ? error.message : 'Failed to load the inference configuration';
        setState({ status: 'error', message, isAuthError: error instanceof ApiError && error.isAuthError });
      });

    void client
      .getInferenceRolePolicy()
      .then((response) => {
        if (active) setRolePolicy({ status: 'loaded', rolePolicy: response.rolePolicy });
      })
      .catch((error: unknown) => {
        if (!active) return;
        const message =
          error instanceof Error
            ? error.message
            : 'The role policy is not available for this account yet.';
        setRolePolicy({ status: 'unavailable', message });
      });

    return () => {
      active = false;
    };
  }, [client, isAdmin, reloadToken]);

  /** Re-read. Only ever called after the API confirmed a write. */
  function reload(): void {
    setReloadToken((token) => token + 1);
  }

  if (!isAdmin) {
    return (
      <section>
        <h1>Inference</h1>
        {/*
          Requirement 13.5. An explanation rather than an error, and rather than an empty page: every
          `inference-config:*` action is admin-only, so a `GET` would earn a `403`, and "Forbidden" tells
          an editor something is broken when the true answer is that this belongs to an administrator.
          No External_Id and no policy is named here, because neither was fetched.
        */}
        <p role="note" className="muted" data-testid="admin-only-notice">
          Inference configuration is managed by an account administrator. Your role in this account
          cannot read or change where model invocations run, so nothing is shown here — not the External
          ID, and not the role policy. Ask an administrator of {accountId ?? 'this account'} if this
          account should process documents in your own AWS account.
        </p>
      </section>
    );
  }

  if (state.status === 'loading') {
    return (
      <section>
        <h1>Inference</h1>
        <LoadingState label="Loading the inference configuration…" />
      </section>
    );
  }

  if (state.status === 'error') {
    return (
      <section>
        <h1>Inference</h1>
        <ErrorState
          summary="Could not load the inference configuration."
          message={state.message}
          isAuthError={state.isAuthError}
        />
      </section>
    );
  }

  const { configuration } = state;
  const currentForm = form ?? formFrom(configuration);
  const globalIds = globalProfileIds(currentForm);
  const currentKey = acknowledgementKeyOf(globalIds);
  // Derived, so changing a `global.` identifier clears the tick with no effect to fire and no stale
  // state to leave behind (Requirement 5.7).
  const acknowledged = globalIds.length > 0 && acknowledgedKey === currentKey;
  const gated = globalIds.length > 0 && !acknowledged;

  function updateForm(next: Partial<FormState>): void {
    setForm({ ...currentForm, ...next });
  }

  function updatePin(sourceType: SourceType, next: Partial<PinDraft>): void {
    updateForm({
      pins: { ...currentForm.pins, [sourceType]: { ...currentForm.pins[sourceType], ...next } },
    });
  }

  function submit(): void {
    // Guarded as well as `aria-disabled`: the requirement is that no request is *issued* before the
    // acknowledgement (Requirement 5.5), and an attribute is the presentation of that rule, not the
    // rule. A programmatic submit, or one racing a re-render, still gets nowhere.
    if (gated || replace.state.submitting) return;
    setNotice(undefined);
    void replace.run(
      () =>
        client.putInferenceConfiguration({
          inferenceConfiguration: buildPayload(currentForm, acknowledged ? globalIds : []),
        }),
      (response) => {
        setNotice(
          `Saved. This account is now ${response.inferenceConfiguration.mode === 'customer_hosted' ? 'customer-hosted' : 'Soroush-hosted'}, and its verification state is ${VERIFICATION_LABELS[response.inferenceConfiguration.verificationState].toLowerCase()}.`,
        );
        reload();
      },
    );
  }

  function requestVerification(): void {
    setNotice(undefined);
    void verify.run(
      () => client.verifyInferenceConfiguration(),
      (response) => {
        setNotice(
          response.inferenceConfiguration.verificationState === 'verified'
            ? 'Verified. Role assumption succeeded and every selected inference profile honoured a forced tool call.'
            : 'Verification ran and did not pass. The fault is below.',
        );
        reload();
      },
    );
  }

  function confirmRotate(): void {
    void rotate.run(
      () => client.rotateInferenceExternalId(),
      (response) => {
        setPending(undefined);
        // The advisory travels with the response, so this page and the CLI cannot word it differently.
        setNotice(response.advisory);
        reload();
      },
    );
  }

  return (
    <section>
      <h1>Inference</h1>
      <p className="muted" data-testid="account-line">
        This configuration decides which AWS account processes documents for{' '}
        <strong>{accountId ?? 'this account'}</strong>, and whose bill the model invocations land on.
      </p>

      {notice !== undefined ? (
        <p role="status" className="muted" data-testid="inference-notice">
          {notice}
        </p>
      ) : null}

      <InferenceStatusPanel configuration={configuration} />

      {configuration.failure !== undefined ? (
        <InferenceFailurePanel
          failure={configuration.failure}
          onVerify={requestVerification}
          verifying={verify.state.submitting}
          verifyError={verify.state.errorMessage}
        />
      ) : null}

      <ExternalIdPanel
        configuration={configuration}
        onRotate={() => {
          rotate.reset();
          setNotice(undefined);
          setPending({ kind: 'rotate-external-id' });
        }}
      />

      <RolePolicySection state={rolePolicy} />

      <EffectiveLimitsPanel configuration={configuration} />

      {configuration.probes.length > 0 ? <ProbesTable probes={configuration.probes} /> : null}

      <h2>Change the configuration</h2>
      <form
        data-testid="inference-form"
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <FormField
          label="Inference mode"
          path={`${ROOT}.mode`}
          errors={replace.state.fieldErrors}
          hint="Soroush-hosted runs inference in Soroush’s account on Soroush’s bill. Customer-hosted runs it in a region you name, in an account you own, on your bill."
          render={(props) => (
            <select
              {...props}
              value={currentForm.mode}
              onChange={(event) => updateForm({ mode: event.target.value as InferenceMode })}
            >
              {INFERENCE_MODES.map((mode) => (
                <option key={mode} value={mode}>
                  {MODE_LABELS[mode]}
                </option>
              ))}
            </select>
          )}
        />

        <FormField
          label="Customer inference role ARN"
          path={`${ROOT}.customerRoleArn`}
          errors={replace.state.fieldErrors}
          hint="A role in your own AWS account, named soroush-inference-… — the prefix the Inference Principal’s own policy admits. Retained if you switch back to Soroush-hosted."
          render={(props) => (
            <input
              {...props}
              type="text"
              value={currentForm.customerRoleArn}
              onChange={(event) => updateForm({ customerRoleArn: event.target.value })}
            />
          )}
        />

        <FormField
          label="Customer inference region"
          path={`${ROOT}.customerRegion`}
          errors={replace.state.fieldErrors}
          hint="The AWS region inference executes in. You choose it, and Soroush does not override it."
          render={(props) => (
            <input
              {...props}
              type="text"
              value={currentForm.customerRegion}
              onChange={(event) => updateForm({ customerRegion: event.target.value })}
            />
          )}
        />

        <FormField
          label="Primary model"
          path={`${ROOT}.profiles.primary`}
          errors={replace.state.fieldErrors}
          hint="The inference profile every first attempt targets."
          render={(props) => (
            <input
              {...props}
              type="text"
              value={currentForm.primary}
              onChange={(event) => updateForm({ primary: event.target.value })}
            />
          )}
        />

        <FormField
          label="Escalation model"
          path={`${ROOT}.profiles.escalation`}
          errors={replace.state.fieldErrors}
          hint="The inference profile every self-correction attempt targets. Required together with the primary: a pair with one member resolves an escalation to nothing at the moment it is needed."
          render={(props) => (
            <input
              {...props}
              type="text"
              value={currentForm.escalation}
              onChange={(event) => updateForm({ escalation: event.target.value })}
            />
          )}
        />

        <h3>Model pins by source type</h3>
        <p className="muted">
          Optional overrides. Each tier falls back independently, so pinning a source type’s primary and
          leaving its escalation blank is a supported configuration, not a half-finished one. A source
          type with no pin uses the models above.
        </p>
        {SOURCE_TYPES.map((sourceType) => (
          <div key={sourceType} data-testid={`pin-${sourceType}`}>
            <FormField
              label={`Primary model for ${sourceType}`}
              path={`${ROOT}.pins.${sourceType}.primary`}
              errors={replace.state.fieldErrors}
              render={(props) => (
                <input
                  {...props}
                  type="text"
                  value={currentForm.pins[sourceType].primary}
                  onChange={(event) => updatePin(sourceType, { primary: event.target.value })}
                />
              )}
            />
            <FormField
              label={`Escalation model for ${sourceType}`}
              path={`${ROOT}.pins.${sourceType}.escalation`}
              errors={replace.state.fieldErrors}
              render={(props) => (
                <input
                  {...props}
                  type="text"
                  value={currentForm.pins[sourceType].escalation}
                  onChange={(event) => updatePin(sourceType, { escalation: event.target.value })}
                />
              )}
            />
          </div>
        ))}

        {globalIds.length > 0 ? (
          <GeographyAcknowledgementControl
            globalIds={globalIds}
            region={currentForm.customerRegion.trim()}
            acknowledged={acknowledged}
            onChange={(next) => setAcknowledgedKey(next ? currentKey : undefined)}
          />
        ) : null}

        <FormErrorPanel
          message={replace.state.errorMessage}
          unmapped={unmappedFieldErrors(replace.state.fieldErrors, mappedPaths())}
        />

        <div className="form-actions">
          {/*
            `aria-disabled` rather than `disabled`, deliberately. A `disabled` button is removed from the
            tab order and its `aria-describedby` is never announced, so the *reason* the control is held
            back would reach a sighted reader and nobody else — and "conveyed by position or colour
            alone" is exactly what the gate must not be. Kept focusable, described by the note the
            checkbox owns, and refused in {@link submit}: the rule is enforced in the handler, and the
            attribute only presents it.
          */}
          <button
            type="submit"
            aria-disabled={gated || replace.state.submitting}
            {...(gated ? { 'aria-describedby': ACKNOWLEDGEMENT_GATE_ID } : {})}
            data-testid="save-inference-configuration"
          >
            {replace.state.submitting ? 'Saving…' : 'Save configuration'}
          </button>
          <button
            type="button"
            className="secondary"
            disabled={verify.state.submitting}
            onClick={requestVerification}
          >
            {verify.state.submitting ? 'Verifying…' : 'Request verification'}
          </button>
        </div>
      </form>

      {pending !== undefined ? (
        <ConfirmDialog
          title="Rotate the External ID"
          message="Rotating issues a replacement External ID and sets the verification state to unverified. The sts:ExternalId condition on your role’s trust policy stops matching immediately, so customer-hosted generation is refused until you update the trust policy to the new value and verify again. The inference mode does not change."
          confirmLabel="Rotate External ID"
          busy={rotate.state.submitting}
          errorMessage={rotate.state.errorMessage}
          onConfirm={confirmRotate}
          onCancel={() => {
            rotate.reset();
            setPending(undefined);
          }}
        />
      ) : null}
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/* Status, External_Id, policies, limits, probes                                */
/* -------------------------------------------------------------------------- */

/** Who made the most recent change, from the actor the API attributed it to. */
function actorLabel(configuration: InferenceConfigurationDto): string {
  const actor = configuration.changedBy;
  if (actor === undefined) return 'nobody yet — this is the platform default';
  return actor.kind === 'user' ? `user ${actor.userId}` : `API key ${actor.keyId}`;
}

/** Epoch seconds as a readable instant; the API deals in seconds throughout this plane. */
function atEpochLabel(epochSeconds: number): string {
  return new Date(epochSeconds * 1000).toISOString();
}

function InferenceStatusPanel({
  configuration,
}: {
  configuration: InferenceConfigurationDto;
}): JSX.Element {
  return (
    <section className="card" data-testid="inference-status">
      <h2>Current configuration</h2>
      <dl className="detail-list">
        <div className="detail-row">
          <dt>Inference mode</dt>
          <dd data-testid="inference-mode">{MODE_LABELS[configuration.mode]}</dd>
        </div>
        <div className="detail-row">
          <dt>Verification state</dt>
          <dd data-testid="verification-state">
            {VERIFICATION_LABELS[configuration.verificationState]}
          </dd>
        </div>
        <div className="detail-row">
          <dt>Inference profiles</dt>
          <dd data-testid="configured-profiles">
            {configuration.profiles === undefined
              ? 'None configured'
              : `${configuration.profiles.primary} → ${configuration.profiles.escalation}`}
          </dd>
        </div>
        <div className="detail-row">
          <dt>Customer role</dt>
          <dd>{configuration.customerRoleArn ?? 'None configured'}</dd>
        </div>
        <div className="detail-row">
          <dt>Customer region</dt>
          <dd>{configuration.customerRegion ?? 'None configured'}</dd>
        </div>
        <div className="detail-row">
          <dt>Last changed</dt>
          <dd>
            {atEpochLabel(configuration.changedAtEpoch)} by {actorLabel(configuration)}
          </dd>
        </div>
      </dl>
    </section>
  );
}

/**
 * The External_Id, unmasked, with a copy control — and the rotation that replaces it.
 *
 * Rendered through {@link CopyableSnippet} rather than as text, because the value's only purpose is to
 * be pasted into a trust policy: the copy control is the interaction, and that component is the one
 * place on the dashboard that tells the truth about whether a copy actually happened rather than
 * reporting success from an optional-chained call that copied nothing.
 */
function ExternalIdPanel({
  configuration,
  onRotate,
}: {
  configuration: InferenceConfigurationDto;
  onRotate: () => void;
}): JSX.Element {
  return (
    <section className="card" data-testid="external-id-panel">
      <h2>External ID</h2>
      <p className="muted">
        Soroush generates this and returns it unmasked, because you cannot write the{' '}
        <code>sts:ExternalId</code> condition on your role’s trust policy without reading it. It is not
        a credential: an attacker holding it also has to <em>be</em> the Inference Principal below. It is
        excluded from every error response and every log record.
      </p>
      <CopyableSnippet label="External ID" text={configuration.externalId} testId="external-id" />
      <dl className="detail-list">
        <div className="detail-row">
          <dt>Inference Principal</dt>
          <dd data-testid="inference-principal-arn">{configuration.inferencePrincipalArn}</dd>
        </div>
      </dl>
      <p className="muted">
        The one principal every Customer_Inference_Role admits. Name it in your trust policy, and
        condition on the External ID above.
      </p>
      <div className="form-actions">
        <button type="button" className="secondary" onClick={onRotate}>
          Rotate External ID
        </button>
      </div>
    </section>
  );
}

/**
 * The Published_Role_Policy: the two documents to put on the role, and the Terraform that does both.
 *
 * Two separate copyable blocks rather than one, because they go to two different places — a trust
 * policy and an inline permission policy are different fields in the console and different arguments in
 * the API — and a reader who pastes a concatenation of them into either gets an opaque IAM error.
 */
function RolePolicySection({ state }: { state: RolePolicyState }): JSX.Element {
  if (state.status === 'loading') {
    return (
      <section className="card">
        <h2>Role policy</h2>
        <LoadingState label="Loading the role policy…" />
      </section>
    );
  }

  if (state.status === 'unavailable') {
    return (
      <section className="card" data-testid="role-policy-unavailable">
        <h2>Role policy</h2>
        <p role="note" className="muted">
          The permission policy names one resource per inference profile per region, so it cannot be
          rendered until this account has a customer role, a region, and a profile selection saved
          below. The two values you need first — the External ID and the Inference Principal — are
          above, and together they are the whole trust policy.
        </p>
        <p className="muted">{state.message}</p>
      </section>
    );
  }

  const { rolePolicy } = state;
  return (
    <section className="card" data-testid="role-policy">
      <h2>Role policy</h2>
      {rolePolicy.advisory !== undefined ? (
        <div role="alert" className="error-panel" data-testid="role-policy-advisory">
          <p>{rolePolicy.advisory}</p>
        </div>
      ) : null}
      <dl className="detail-list">
        <div className="detail-row">
          <dt>Role name</dt>
          <dd data-testid="role-name">{rolePolicy.roleName}</dd>
        </div>
      </dl>
      <CopyableSnippet
        label="Trust policy"
        text={JSON.stringify(rolePolicy.trustPolicy, null, 2)}
        testId="inference-trust-policy"
      />
      <CopyableSnippet
        label="Permission policy"
        text={JSON.stringify(rolePolicy.permissionPolicy, null, 2)}
        testId="inference-permission-policy"
      />
      <p className="muted">
        A cross-region inference profile authorizes against the profile ARN <em>and</em> the underlying
        foundation-model ARN in whichever region it routes to, which is why the permission policy names
        both. A policy carrying only the profile applies cleanly and then fails the first real
        invocation.
      </p>
      <CopyableSnippet
        label="Terraform module"
        text={rolePolicy.terraformModule}
        testId="inference-terraform-module"
      />
    </section>
  );
}

/**
 * The Requirement 10.2 spend guards, as read-only figures.
 *
 * A definition list and **no control**: these are displayed, never set (**OI-D2**). They apply in both
 * Inference_Modes, which is the part worth stating out loud — a customer paying for their own inference
 * is still bounded by the platform's rate, concurrency, and token caps, and that is what stops a
 * misconfiguration from running up their bill.
 */
function EffectiveLimitsPanel({
  configuration,
}: {
  configuration: InferenceConfigurationDto;
}): JSX.Element {
  const limits = configuration.effectiveLimits;
  return (
    <section className="card" data-testid="effective-limits">
      <h2>Spend guards in force</h2>
      <p className="muted">
        Shown, not settable. These are the platform’s own limits and they apply in both inference modes,
        so a misconfiguration cannot run up your bill. Ask Soroush if you need them changed.
      </p>
      <dl className="detail-list">
        <div className="detail-row">
          <dt>Generation requests per hour</dt>
          <dd data-testid="limit-rate-per-hour">{limits.ratePerHour}</dd>
        </div>
        <div className="detail-row">
          <dt>Concurrent generation requests</dt>
          <dd data-testid="limit-max-concurrent">{limits.maxConcurrent}</dd>
        </div>
        <div className="detail-row">
          <dt>Input tokens per attempt</dt>
          <dd data-testid="limit-max-input-tokens">{limits.maxInputTokens}</dd>
        </div>
        <div className="detail-row">
          <dt>Output tokens per attempt</dt>
          <dd data-testid="limit-max-output-tokens">{limits.maxOutputTokens}</dd>
        </div>
      </dl>
    </section>
  );
}

/**
 * What the Model_Capability_Probe found, per Inference_Profile.
 *
 * `stopReason` is shown and is not a verdict: a model may report `end_turn` alongside a well-formed
 * tool call, so the pass condition is the tool call and this column is evidence rather than criterion.
 * `modalities` reads `unknown` when the platform has no row for the underlying foundation model, which
 * is a recorded fact and never a refusal.
 */
function ProbesTable({ probes }: { probes: readonly ProbeOutcome[] }): JSX.Element {
  return (
    <section className="card" data-testid="probe-outcomes">
      <h2>Capability probes</h2>
      <table aria-label="Capability probes">
        <thead>
          <tr>
            <th scope="col">Inference profile</th>
            <th scope="col">Result</th>
            <th scope="col">Stop reason</th>
            <th scope="col">Modalities</th>
            <th scope="col">Checked</th>
          </tr>
        </thead>
        <tbody>
          {probes.map((probe) => (
            <tr key={`${probe.profileId}:${probe.atEpoch}`}>
              <td>{probe.profileId}</td>
              <td>
                {probe.result === 'passed'
                  ? 'Passed'
                  : `Failed${probe.failure === undefined ? '' : ` (${probe.failure})`}`}
              </td>
              <td>{probe.stopReason ?? '—'}</td>
              <td>{probe.modalities === 'unknown' ? 'unknown' : probe.modalities.join(', ')}</td>
              <td>{atEpochLabel(probe.atEpoch)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/* The failure (Requirements 7.4, 7.6, 7.7, 7.9)                               */
/* -------------------------------------------------------------------------- */

/**
 * The recorded Inference_Failure, stated as a fault with what to change in the customer's own account.
 *
 * ## What this renders, and what it structurally cannot
 *
 * It renders the failure kind, the role ARN, the region, the Inference_Profile concerned, and the
 * provider's exception **class** — and it renders no External_Id and no provider **message**
 * (Requirements 7.6, 7.9). That is not restraint at the call site: {@link InferenceFailureDto} has no
 * `message` field and no `externalId` field, because the server-side type has neither, so there is
 * nothing here to leak. STS and Bedrock messages routinely quote the request that provoked them, and on
 * this surface that request carries the customer's own API documentation.
 *
 * ## `probe_throttled` is "not yet verified", not "broken"
 *
 * A throttled probe means the platform could not reach a verdict. Calling that a fault would send an
 * administrator to look for a misconfiguration that is not there — and the fix, if any, is a quota in
 * their account or simply waiting. So it renders as a `role="status"` that offers the verify action,
 * while every other arm is a `role="alert"`. The distinction is between "your configuration is wrong"
 * and "we have not been able to tell yet", and it is the whole reason this component branches at all.
 */
function InferenceFailurePanel({
  failure,
  onVerify,
  verifying,
  verifyError,
}: {
  failure: InferenceFailureDto;
  onVerify: () => void;
  verifying: boolean;
  verifyError?: string;
}): JSX.Element {
  const unjudged = isUnjudgedFailure(failure.kind);
  return (
    <section
      // Announced, and announced at the right urgency: an alert for a fault the administrator has to
      // act on, a status for a verdict the platform could not reach.
      role={unjudged ? 'status' : 'alert'}
      className={unjudged ? 'card' : 'error-panel'}
      data-testid="inference-failure"
    >
      <h2>{unjudged ? 'Not yet verified' : 'Inference configuration fault'}</h2>
      <p data-testid="failure-kind">{FAILURE_LABELS[failure.kind]}</p>
      <dl className="detail-list">
        <div className="detail-row">
          <dt>Failure</dt>
          <dd>{failure.kind}</dd>
        </div>
        <div className="detail-row">
          <dt>Customer role</dt>
          <dd data-testid="failure-role-arn">{failure.customerRoleArn ?? 'not reached'}</dd>
        </div>
        <div className="detail-row">
          <dt>Region</dt>
          <dd data-testid="failure-region">{failure.region ?? 'not reached'}</dd>
        </div>
        <div className="detail-row">
          <dt>Inference profile</dt>
          <dd data-testid="failure-profile-id">{failure.inferenceProfileId ?? 'not reached'}</dd>
        </div>
        <div className="detail-row">
          <dt>Provider error class</dt>
          {/* The class, never the message: a provider message quotes the request that provoked it, and
              here that request carries the customer's own documentation (Requirement 7.9). */}
          <dd data-testid="failure-error-class">{failure.errorClass ?? 'none reported'}</dd>
        </div>
      </dl>
      <h3>What to change in your account</h3>
      <p data-testid="failure-remediation">{FAILURE_REMEDIATION[failure.kind]}</p>
      {verifyError !== undefined ? (
        <p className="muted" data-testid="verify-error">
          {verifyError}
        </p>
      ) : null}
      <div className="form-actions">
        <button type="button" className="secondary" disabled={verifying} onClick={onVerify}>
          {verifying ? 'Verifying…' : 'Request verification'}
        </button>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/* The geography acknowledgement (Requirements 5.5, 5.7, 5.8)                  */
/* -------------------------------------------------------------------------- */

/** The id the submit control's `aria-describedby` points at while the gate is closed. */
const ACKNOWLEDGEMENT_GATE_ID = 'geography-acknowledgement-gate';

/**
 * The checkbox an administrator must tick before a `global.`-prefixed profile is accepted
 * (Requirement 5.5).
 *
 * Rendered **only** when the form actually names one, and never pre-ticked (Requirement 5.8). Its
 * `checked` is derived by the page from the identifier set it was ticked for, so changing an identifier
 * clears it (Requirement 5.7) — see the module doc.
 *
 * The label names **both** consequences, because the second one is the surprising half: the request may
 * execute outside the named region's geography, *and* the published permission policy's
 * foundation-model ARN region becomes `*` — a policy the customer applies to their own role, wider than
 * the region they typed. An acknowledgement that named only the first would be consent to something
 * narrower than what happens.
 *
 * Implicit labelling, matching {@link ProvenanceNotice} and `SharingDisclosure`: no `id` to collide,
 * and still reachable by its accessible name.
 */
function GeographyAcknowledgementControl({
  globalIds,
  region,
  acknowledged,
  onChange,
}: {
  globalIds: readonly string[];
  region: string;
  acknowledged: boolean;
  onChange: (acknowledged: boolean) => void;
}): JSX.Element {
  const where = region.length === 0 ? 'the region you name' : region;
  return (
    <div role="note" className="adoption-notice" data-testid="geography-acknowledgement">
      <p>
        This configuration names {globalIds.length === 1 ? 'a profile' : 'profiles'} whose routing is
        not visible in the identifier:
      </p>
      <ul data-testid="geography-profile-ids">
        {globalIds.map((id) => (
          <li key={id}>
            <code>{id}</code>
          </li>
        ))}
      </ul>
      <p className="muted" id={ACKNOWLEDGEMENT_GATE_ID}>
        Saving is held until you acknowledge this. The acknowledgement covers the exact{' '}
        {globalIds.length === 1 ? 'identifier' : 'identifiers'} listed above and no other, so changing
        one clears it.
      </p>
      <label className="checkbox-row">
        <input
          type="checkbox"
          checked={acknowledged}
          data-testid="acknowledge-geography"
          onChange={(event) => onChange(event.target.checked)}
        />
        <span>
          I acknowledge that a request using {globalIds.length === 1 ? 'this profile' : 'these profiles'}{' '}
          may execute outside {where}’s geography, and that the published permission policy’s
          foundation-model ARN region becomes <code>*</code> as a result.
        </span>
      </label>
    </div>
  );
}
